/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { Button } from '../../ui/Button';
import { Prompt } from '../../../types/prompts';

interface PromptEditorModalProps {
    prompt?: Prompt;
    onClose: () => void;
    onSave: (prompt: Omit<Prompt, 'id' | 'isCustom'> & { id?: string }) => void;
}

export const PromptEditorModal: React.FC<PromptEditorModalProps> = ({ prompt, onClose, onSave }) => {
    const [title, setTitle] = useState(prompt?.title || '');
    const [text, setText] = useState(prompt?.text || '');
    const [category, setCategory] = useState(prompt?.category || 'General');

    const handleSave = () => {
        if (title && text) {
            onSave({ id: prompt?.id, title, text, category: category as Prompt['category'] });
            onClose();
        }
    };

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-white dark:bg-[#1A1D23] rounded-xl shadow-2xl w-full max-w-lg border border-stone-200 dark:border-[#2D3142]">
                <div className="p-4 border-b border-stone-100 dark:border-white/5 flex justify-between items-center">
                    <h3 className="font-serif text-lg text-stone-900 dark:text-white">{prompt ? 'Edit Prompt' : 'Create New Prompt'}</h3>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors"><X size={20} /></button>
                </div>
                <div className="p-6 space-y-4">
                    <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">Title</label>
                        <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full mt-1 p-2 bg-stone-50 dark:bg-white/5 text-stone-900 dark:text-white rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none" placeholder="e.g., Weekly Sales Report" />
                    </div>
                     <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">Category</label>
                        {/* @google/genai-fix: Cast select value to the correct union type to satisfy TypeScript. */}
                        <select value={category} onChange={e => setCategory(e.target.value as Prompt['category'])} className="w-full mt-1 p-2 bg-stone-50 dark:bg-white/5 text-stone-900 dark:text-white rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none">
                            <option>General</option>
                            <option>Analytics</option>
                            <option>Transactions</option>
                            <option>Management</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-stone-500 dark:text-gray-400 uppercase tracking-wider">Prompt Text</label>
                        <textarea value={text} onChange={e => setText(e.target.value)} className="w-full mt-1 p-2 bg-stone-50 dark:bg-white/5 text-stone-900 dark:text-white rounded-lg border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none h-32 resize-none" placeholder="Enter the prompt you want the AI to execute..."></textarea>
                    </div>
                </div>
                <div className="p-4 bg-stone-50 dark:bg-[#0A0B0F] border-t border-stone-100 dark:border-white/5 flex justify-end gap-3">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} icon={<Save size={16}/>}>Save Prompt</Button>
                </div>
            </motion.div>
        </motion.div>
    );
};